import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SiteContentContext = createContext(null);

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchContent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/content`);
      setContent(data);
    } catch (e) {
      console.error('Failed to load site content, using defaults:', e);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const getText = useCallback((key, fallback) => {
    if (content[key] != null) return content[key];
    return fallback;
  }, [content]);

  const updateText = useCallback(async (key, value) => {
    await axios.put(
      `${BACKEND_URL}/api/content/${key}`,
      { value },
      { withCredentials: true }
    );
    setContent((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleEditMode = useCallback(() => setEditMode((v) => !v), []);
  const exitEditMode = useCallback(() => setEditMode(false), []);

  return (
    <SiteContentContext.Provider value={{ content, editMode, toggleEditMode, exitEditMode, getText, updateText, loaded }}>
      {children}
    </SiteContentContext.Provider>
  );
};

export const useSiteContent = () => {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error('useSiteContent must be used within SiteContentProvider');
  return ctx;
};
